// shared/numeric/numeric-mask.directive.ts

import {
    booleanAttribute,
    ComponentRef,
    Directive,
    ElementRef,
    forwardRef,
    inject,
    input,
    isDevMode,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewContainerRef,
    effect,
    signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Maskito } from '@maskito/core';
import { maskitoParseNumber } from '@maskito/kit';

import { NumericFieldConfig } from './numeric.types';
import { NumericLocaleService } from './numeric-locale.service';
import { NumericMaskitoFactory } from './numeric-maskito.factory';
import { NumericStepperComponent } from './numeric-stepper.component';

/**
 * Applies Maskito-driven numeric masking to a native `<input matInput>` and bridges
 * the masked string view <-> a `number | null` model in Angular Reactive Forms.
 *
 * Usage:
 *   <input matInput [appNumericMask]="config" [formControl]="ctrl" />
 *
 * Key invariants:
 *  - The Angular form control value is always `number | null` — never a formatted string.
 *  - Locale is injected (not an @Input); reactive to runtime changes.
 *  - Prefix/suffix belong in the Material template (`matTextPrefix`/`matTextSuffix`).
 *    If a caller does put them in `NumericFieldConfig`, `format()` mirrors Maskito's
 *    behavior so the displayed text and the mask agree.
 *  - The host MUST stay `type="text"`. `type="number"` silently discards any value the
 *    browser can't parse as a plain float (grouping separators, `%`, currency symbols)
 *    and throws on the caret APIs Maskito needs. ArrowUp/ArrowDown stepping and the
 *    spinner buttons are therefore provided here instead; use `inputmode` for the
 *    mobile keypad. The buttons are opt-out via `config.showStepper` or
 *    `[showStepper]="false"`; ArrowUp/ArrowDown keep stepping either way. The spinner
 *    (and its reserved padding) only appear while the input is focused, so an unfocused
 *    field's value stays fully right-aligned.
 *
 * This directive replaces the built-in `DefaultValueAccessor` on the input. Angular
 * automatically prefers a non-built-in `NG_VALUE_ACCESSOR`, so `MatInput` continues
 * to work unchanged.
 */
@Directive({
    selector: 'input[matInput][appNumericMask]',
    standalone: true,
    exportAs: 'appNumericMask',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NumericMaskDirective),
            multi: true,
        },
    ],
})
export class NumericMaskDirective implements ControlValueAccessor, OnInit, OnDestroy {
    readonly appNumericMask = input.required<NumericFieldConfig>();

    /**
     * Per-template override for `config.showStepper`: either source can hide the up/down
     * spinner buttons. Hiding them never disables ArrowUp/ArrowDown stepping.
     */
    readonly showStepper = input(true, { transform: booleanAttribute });

    private readonly host = inject<ElementRef<HTMLInputElement>>(ElementRef);
    private readonly renderer = inject(Renderer2);
    private readonly viewContainer = inject(ViewContainerRef);
    private readonly localeService = inject(NumericLocaleService);
    private readonly factory = inject(NumericMaskitoFactory);

    private readonly _disabled = signal(false);

    /** Lets a spinner UI mirror the control's disabled state. */
    readonly disabled = this._disabled.asReadonly();

    private maskito: Maskito | null = null;
    private stepperRef: ComponentRef<NumericStepperComponent> | null = null;

    /** Truthy source of the current numeric model — kept in sync with the form control. */
    private currentValue: number | null = null;
    private lastValidValue: number | null = null;

    private onChange: (value: number | null) => void = () => { };
    private onTouched: () => void = () => { };

    private readonly disposeInputListener: () => void;
    private readonly disposeBlurListener: () => void;
    private readonly disposeKeydownListener: () => void;
    private readonly stepperListeners: Array<() => void> = [];

    constructor() {
        this.enforceTextType();

        // Rebuild the Maskito instance whenever config, locale, or precision changes and
        // re-render the current model so separators/prefix/suffix/scale flip instantly.
        effect(() => {
            const cfg = this.appNumericMask();
            const locale = this.localeService.locale();
            const precision = this.localeService.precision();
            this.rebuildMask(cfg, locale, precision);
            this.render(this.currentValue);
        });

        this.disposeInputListener = this.renderer.listen(
            this.host.nativeElement,
            'input',
            () => this.onNativeInput(),
        );

        this.disposeBlurListener = this.renderer.listen(
            this.host.nativeElement,
            'blur',
            () => this.onBlur(),
        );

        this.disposeKeydownListener = this.renderer.listen(
            this.host.nativeElement,
            'keydown',
            (event: KeyboardEvent) => this.onKeyDown(event),
        );
    }

    ngOnInit(): void {
        if (this.showStepper() && (this.appNumericMask().showStepper ?? true)) this.attachStepper();
    }

    // --- ControlValueAccessor ------------------------------------------------

    writeValue(value: unknown): void {
        const normalized = this.normalize(value);
        // Skip re-render when Reactive Forms echoes back the value we just emitted:
        // Maskito already produced the displayed text and moving the caret would be jarring.
        if (normalized === this.currentValue) return;
        // clamp normalized value to the allowed range
        const clamped = normalized === null ? null : this.clamp(normalized);
        this.currentValue = clamped;
        this.lastValidValue = clamped;
        this.render(clamped);
    }

    registerOnChange(fn: (value: number | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this._disabled.set(isDisabled);
        this.renderer.setProperty(this.host.nativeElement, 'disabled', isDisabled);
    }

    ngOnDestroy(): void {
        this.disposeInputListener?.();
        this.disposeBlurListener?.();
        this.disposeKeydownListener?.();
        this.stepperListeners.forEach(dispose => dispose());
        this.stepperRef?.destroy();
        this.stepperRef = null;
        this.maskito?.destroy();
        this.maskito = null;
    }

    // --- Public stepping API (keyboard + spinner buttons) --------------------

    /** Increments by one step, mirroring `HTMLInputElement.stepUp()`. */
    stepUp(): void {
        this.stepBy(1);
    }

    /** Decrements by one step, mirroring `HTMLInputElement.stepDown()`. */
    stepDown(): void {
        this.stepBy(-1);
    }

    focusInput(): void {
        this.host.nativeElement.focus();
    }

    // --- Internals -----------------------------------------------------------

    private onNativeInput(): void {
        const raw = this.host.nativeElement.value;
        const parsed = this.parse(raw);

        if (this.appNumericMask().revertToLastValidValue && parsed === null) {
            return;
        }
        if (parsed === this.currentValue) return;
        this.currentValue = parsed;
        if (this.isValid()) {
            this.lastValidValue = this.currentValue;
        }
        this.onChange(parsed);
    }

    private onBlur(): void {
        this.onTouched();
        const parsedAtBlur = this.parse(this.host.nativeElement.value);
        if (parsedAtBlur === null && this.appNumericMask().revertToLastValidValue) {
            this.render(this.currentValue);
            return;
        }

        // Clamp out-of-range values to the nearest bound only on blur, so the user is
        // free to overshoot while typing. Empty values are left as null for the validator.
        if (this.appNumericMask().revertToLastValidValue && !this.isValid()) {
            this.currentValue = this.lastValidValue;
            this.onChange(this.currentValue);
            this.host.nativeElement.dispatchEvent(new Event('change'));
        }
        if (this.currentValue === null) return;
        const clamped = this.clamp(this.currentValue);
        if (clamped !== this.currentValue) {
            this.currentValue = clamped;
            this.onChange(clamped);
        }
        // Always re-render on blur so trailing zeros are added (e.g. `50` -> `50.00`).
        this.render(this.currentValue);
    }

    private onKeyDown(event: KeyboardEvent): void {
        if (!this.showStepper()) return;
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown' && event.key !== 'Enter') return;
        const el = this.host.nativeElement;
        if (el.disabled || el.readOnly) return;
        // Suppress the native caret jump to the start/end of the text.
        event.preventDefault();
        if (event.key === 'Enter') {
            this.onBlur();
            return;
        }
        this.stepBy(event.key === 'ArrowUp' ? 1 : -1);
    }

    private stepBy(direction: 1 | -1): void {
        const el = this.host.nativeElement;
        if (el.disabled || el.readOnly) return;

        const cfg = this.appNumericMask();
        const scale = this.effectiveScale(cfg);
        const step = cfg.step ?? 10 ** -scale;
        // An empty field steps off zero, matching native `<input type="number">`.
        const base = this.currentValue ?? 0;
        const next = this.clamp(this.roundToScale(base + direction * step, scale));
        if (next === this.currentValue) return;

        this.currentValue = next;
        this.lastValidValue = this.currentValue;
        this.render(next);
        this.placeCaretBeforeSuffix();
        this.onChange(next);
    }

    private clamp(value: number): number {
        const cfg = this.appNumericMask();
        let clamped = value;
        if (typeof cfg.max === 'number' && clamped > cfg.max) clamped = cfg.max;
        if (typeof cfg.min === 'number' && clamped < cfg.min) clamped = cfg.min;
        if (!(cfg.allowNegative ?? false) && clamped < 0) clamped = 0;
        return clamped;
    }

    /**
     * Creates the spinner and parks it over the input's trailing edge, so consumers get
     * the buttons from the directive alone without touching their templates. The spinner
     * — and the padding reserved for it — only appear while the input is focused, so an
     * unfocused field keeps its value fully right-aligned with no dead space.
     */
    private attachStepper(): void {
        const el = this.host.nativeElement;
        // `createComponent` inserts the host element as the input's next sibling — i.e.
        // inside the Material infix, which is already `position: relative`.
        this.stepperRef = this.viewContainer.createComponent(NumericStepperComponent);
        this.stepperRef.setInput('for', this);

        const stepperEl = this.stepperRef.location.nativeElement as HTMLElement;

        const reveal = () => {
            stepperEl.classList.add('numeric-stepper--visible');
        this.renderer.setStyle(el, 'padding-inline-end', '18px');
        };
        const hide = () => {
            stepperEl.classList.remove('numeric-stepper--visible');
            this.renderer.removeStyle(el, 'padding-inline-end');
        };

        const container = el.parentElement ?? el;
        this.stepperListeners.push(
            this.renderer.listen(container, 'mouseenter', reveal),
            this.renderer.listen(container, 'mouseleave', () => {
                if (document.activeElement !== el) hide();
            }),
            this.renderer.listen(el, 'focus', reveal),
            this.renderer.listen(el, 'blur', hide),
        );
    }

    /** Strips the binary-float drift that `0.1 + 0.2` style accumulation introduces. */
    private roundToScale(value: number, scale: number): number {
        return Number(value.toFixed(Math.min(20, Math.max(0, scale))));
    }

    private effectiveScale(cfg: NumericFieldConfig): number {
        return cfg.scale ?? (cfg.kind === 'integer' ? 0 : this.localeService.precision());
    }

    private placeCaretBeforeSuffix(): void {
        const el = this.host.nativeElement;
        if (document.activeElement !== el) return;
        const { suffix } = this.factory.resolveAffixes(this.appNumericMask(), this.localeService.locale());
        const caret = Math.max(0, el.value.length - suffix.length);
        el.setSelectionRange(caret, caret);
    }

    /**
     * `type="number"` blanks out any value the browser can't parse as a bare float and
     * makes `setSelectionRange` throw, which breaks both the mask and stepping.
     */    private enforceTextType(): void {
        const el = this.host.nativeElement;
        if (el.type === 'text') return;
        if (isDevMode()) {
            console.warn(
                `[appNumericMask] input type="${el.type}" is not supported and has been forced to "text". ` +
                'Use inputmode="numeric"/"decimal" for the mobile keypad; ArrowUp/ArrowDown stepping is built in.',
            );
        }
        this.renderer.setProperty(el, 'type', 'text');
    }

    private rebuildMask(config: NumericFieldConfig, locale: string, precision: number): void {
        this.maskito?.destroy();
        const options = this.factory.build(config, locale, precision);
        this.maskito = new Maskito(this.host.nativeElement, options);
    }

    private render(value: number | null): void {
        const el = this.host.nativeElement;
        const nextText = value === null ? '' : this.format(value);
        if (el.value !== nextText) {
            // setProperty avoids attribute/property drift issues and does not trigger
            // an 'input' event, so we don't re-enter `onNativeInput`.
            this.renderer.setProperty(el, 'value', nextText);
        }
    }

    private format(value: number): string {
        const cfg = this.appNumericMask();
        const locale = this.localeService.locale();
        const scale = this.effectiveScale(cfg);
        const { prefix, suffix } = this.factory.resolveAffixes(cfg, locale);

        const body = new Intl.NumberFormat(locale, {
            style: 'decimal',
            minimumFractionDigits: Math.max(0, scale),
            maximumFractionDigits: Math.max(0, scale),
            useGrouping: cfg.useGrouping ?? true,
        }).format(value);

        return `${prefix}${body}${suffix}`;
    }

    private parse(text: string): number | null {
        if (!text) return null;
        const cfg = this.appNumericMask();
        const { prefix, suffix } = this.factory.resolveAffixes(cfg, this.localeService.locale());

        let stripped = text;
        if (prefix) stripped = stripped.split(prefix).join('');
        if (suffix) stripped = stripped.split(suffix).join('');
        stripped = stripped.trim();

        if (!stripped || stripped === '-') return null;

        const dec = this.localeService.decimalSeparator();
        const parsed = maskitoParseNumber(stripped, { decimalSeparator: dec });
        return Number.isFinite(parsed) ? parsed : null;
    }

    private normalize(value: unknown): number | null {
        if (value === null || value === undefined || value === '') return null;
        if (typeof value === 'number' && Number.isFinite(value)) return value;
        // Accept numeric strings coming from server payloads / patchValue.
        if (typeof value === 'string') {
            const n = Number(value);
            return Number.isFinite(n) ? n : null;
        }
        return null;
    }
    private isValid() {
        return this.currentValue !== null && this.currentValue !== undefined && !Number.isNaN(this.currentValue) && Number.isFinite(this.currentValue);
    }
}
